import { Form } from 'antd';
import { FormListFieldData } from 'antd/lib';
import styled from 'styled-components';
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor } from '@dnd-kit/core';
import { useSensors } from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { TypeItemFormSyncWaitingV2 } from './ModalSyncWaitingV2';
import SortableItem from './SortableItem';

type Props = {
  fields: FormListFieldData[];
  remove: (index: number) => void;
  move: (from: number, to: number) => void;
};

const ServicesFormList = ({ fields, remove, move }: Props) => {
  const form = Form.useFormInstance();

  const items = (Form.useWatch('items', form) as TypeItemFormSyncWaitingV2[]) || [];
  const ids = items.map((o, i) => {
    const v = String(o?.uniqueId ?? '').trim();
    return v.length ? v : String(fields[i]?.key);
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={({ active, over }) => {
        if (!over) return;
        const oldIndex = ids.indexOf(String(active.id));
        const newIndex = ids.indexOf(String(over.id));
        if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) move(oldIndex, newIndex);
      }}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {fields.map((field) => {
          const formUid = String(
            form.getFieldValue(['items', field.name, 'uniqueId']) ?? ''
          ).trim();
          const id = formUid.length ? formUid : String(field.key);

          const itemName = form.getFieldValue(['items', field.name, 'itemName']);
          const duration = form.getFieldValue(['items', field.name, 'duration']);
          const staffName = form.getFieldValue(['items', field.name, 'staffName']);
          const staffAvatar = form.getFieldValue(['items', field.name, 'staffAvatar']);

          return (
            <ItemServiceWrapper key={id}>
              <Form.Item name={[field.name, 'select']} style={{ width: '100%', marginBottom: 0 }}>
                <SortableItem
                  itemName={itemName}
                  duration={duration}
                  staffName={staffName}
                  staffAvatar={staffAvatar}
                  remove={remove}
                  fieldName={field.name}
                  uniqueId={id}
                />
              </Form.Item>
              <Form.Item
                className='form-item-special'
                name={[field.name, 'staffId']}
                dependencies={[['items', field.name, 'staffId']]}
                rules={[
                  () => ({
                    validator(_, value) {
                      if (!value) return Promise.reject(new Error('Please select a staff'));
                      return Promise.resolve();
                    },
                  }),
                ]}
              />
            </ItemServiceWrapper>
          );
        })}
      </SortableContext>
    </DndContext>
  );
};

export default ServicesFormList;

const ItemServiceWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;
